import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import CoachIdentityCandidatePanel, { REASON_LABEL } from './CoachIdentityCandidatePanel';
import { getIdentityEvaluations } from '../services/coachIdentityApi';
import { evaluationProgressCounts, evaluationRowReviewState, isInReviewScope } from '../utils/coachAuditProgress';

// Non-blocking observations from the API, rendered as short plain sentences. None of these ever
// prevented a save; showing them as errors would teach the auditor to ignore them.
const WARNING_TEXT = {
  coach_not_on_selected_class: 'recorded coach is not on this class in Dash',
  class_has_no_configured_coach: 'class has no configured coach in Dash',
  employee_inactive: 'recorded coach is inactive',
};

function fmtDate(v) {
  return v ? String(v).slice(0, 10) : '—';
}

// The recorded CLASS judgment. A confirmed class shows its session; the other two outcomes show
// their reason and never a class, because none was recorded.
function classLine(e) {
  if (e.resolution_status === 'occurrence_unresolved' || e.resolution_status === 'no_dash_occurrence_expected') {
    const label = e.resolution_status === 'occurrence_unresolved' ? 'Class unresolved' : 'No Dash class expected';
    return (
      <>
        <Chip size="small" label={label} variant="outlined" sx={{ mb: 0.5 }} />
        {e.reason_code && (
          <Typography variant="caption" color="text.secondary" component="div">{REASON_LABEL[e.reason_code] || e.reason_code}</Typography>
        )}
      </>
    );
  }
  return (
    <>
      <Chip size="small" label="Class recorded" color="success" variant="outlined" sx={{ mb: 0.5 }} />
      <Typography variant="caption" color="text.secondary" component="div">
        {e.snapshot_class_label} · {fmtDate(e.snapshot_occurrence_date)} {String(e.snapshot_start_time || '').slice(0, 5)}
      </Typography>
    </>
  );
}

// The recorded COACH judgment. A Form-coach outcome shows the auditor's label (or the raw Form name)
// and always the raw Form name beside it, labelled -- the label is display text, never a Dash name.
function coachLine(e) {
  if (e.coach_resolution === 'form_identity') {
    return e.resolved_form_coach_label
      ? `Form coach: “${e.resolved_form_coach_label}” (auditor label; Form: ${e.typed_coach_name || '—'})`
      : `Form coach: ${e.typed_coach_name || '—'} (as submitted)`;
  }
  if (e.selected_employee_id) return `coach: ${e.snapshot_coach_display_name || e.selected_employee_id} (Dash)`;
  return 'coach: not decided';
}

/**
 * The evaluations under ONE provisional identity.
 *
 * 🔴 EVERY ROW IS KEPT. Repeated evaluations of the same class on the same day are expected — an
 * evaluator may submit more than one, and each is a separate observation someone made. Nothing here
 * de-duplicates, collapses or hides a row.
 *
 * Loaded lazily on expand, because computing this shape for all 523 identities at once joins ~5,000
 * check-ins against a 2M-row occurrence view.
 */
// The next evaluation still needing a conclusion: in scope and not fully reviewed ('Not reviewed' or
// 'Partly'), in list order after the one just saved, wrapping to earlier rows. Never the saved one.
export function nextUnreviewed(evaluations, currentId) {
  const idx = evaluations.findIndex((e) => e.checkin_id === currentId);
  const order = [...evaluations.slice(idx + 1), ...evaluations.slice(0, Math.max(idx, 0))];
  return order.find((e) => e.checkin_id !== currentId && isInReviewScope(e)
    && evaluationRowReviewState(e) !== 'reviewed') || null;
}

// One saved review in words, for the page-level confirmation.
function savedMessage(row, e) {
  const who = (row && row.form_raw_name) || e.typed_coach_name || 'this coach';
  const cls = e.resolution_status === 'occurrence_unresolved'
    ? 'class unresolved'
    : e.resolution_status === 'no_dash_occurrence_expected'
      ? 'no Dash class expected'
      : (e.snapshot_class_label || 'class recorded');
  return `Saved ✓ ${who} · ${fmtDate(e.class_date)} — ${cls}; ${coachLine(e)}`;
}

export default function CoachIdentityEvaluationList({ row, onProgressChange, onIdentityComplete, onEvaluationSaved }) {
  const [evaluations, setEvaluations] = useState(null);
  // Latest callback in a ref, so reporting progress depends on the evaluations alone and a parent
  // re-render can never re-fire it.
  const progressRef = useRef(onProgressChange);
  progressRef.current = onProgressChange;
  const [facilities, setFacilities] = useState([]);
  // From the server, never hard-coded here. Anything but an explicit false reads as pending, so an
  // older API response can never make a saved review claim to be in reporting.
  const [integrationPending, setIntegrationPending] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  // Evaluations saved while this identity has been open. Their rows keep a Saved ✓ tag while rendered.
  const [savedIds, setSavedIds] = useState(() => new Set());
  // Set only when a save ADVANCED to an evaluation: that one is scrolled into view and focused.
  const [advancedTo, setAdvancedTo] = useState(null);
  const rowRefs = useRef(new Map());

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    getIdentityEvaluations({ com_code: row.com_code, typed_name_norm: row.typed_name_norm })
      .then((res) => {
        if (!live) return;
        setEvaluations(res.evaluations || []);
        // Every facility of this franchise, fetched once per identity rather than per evaluation:
        // the panel below opens and closes repeatedly and the list never changes between them.
        setFacilities(res.facilities || []);
        setIntegrationPending(res.reporting_integration_pending !== false);
      })
      .catch((err) => { if (live) setError(err.message || 'Could not load evaluations.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [row.com_code, row.typed_name_norm]);

  const handleResolved = (updated) => {
    setEvaluations((prev) => (prev || []).map((e) => (e.checkin_id === updated.checkin_id ? { ...e, ...updated } : e)));
  };

  // A successful SAVE (never a removal): record it, announce it, and move on at once -- to the next
  // unreviewed evaluation, or, when none remains, close the identity so the active filter can drop it
  // normally. A partly-reviewed save with nothing else left stays open: it is not finished.
  const handleSaved = (updated) => {
    const merged = (evaluations || []).map((e) => (e.checkin_id === updated.checkin_id ? { ...e, ...updated } : e));
    const saved = merged.find((e) => e.checkin_id === updated.checkin_id) || updated;
    setEvaluations(merged);
    // Reported NOW, not left to the progress effect: closing the identity below unmounts this list
    // before that effect would run, and the grid row would keep its old counts -- so a finished
    // identity would never leave the "Has unreviewed" queue.
    if (progressRef.current) progressRef.current(evaluationProgressCounts(merged));
    setSavedIds((prev) => new Set(prev).add(updated.checkin_id));
    if (onEvaluationSaved) onEvaluationSaved(savedMessage(row, saved));
    const next = nextUnreviewed(merged, updated.checkin_id);
    if (next) {
      setOpenId(next.checkin_id);
      setAdvancedTo(next.checkin_id);
    } else if (isInReviewScope(saved) && evaluationRowReviewState(saved) !== 'reviewed') {
      setAdvancedTo(null);
    } else {
      setOpenId(null);
      setAdvancedTo(null);
      if (onIdentityComplete) onIdentityComplete();
    }
  };

  // Bring the evaluation just advanced to into view. jsdom has no scrollIntoView, hence the guard.
  useEffect(() => {
    if (!advancedTo) return;
    const el = rowRefs.current.get(advancedTo);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [advancedTo]);

  // Same rules as the grid's SQL (see utils/coachAuditProgress), over the in-scope rows only.
  const progress = useMemo(() => evaluationProgressCounts(evaluations || []), [evaluations]);
  const outOfScopeCount = (evaluations || []).length - progress.evaluation_count;

  // Keep this identity's grid row in step with what the auditor just saved or removed.
  useEffect(() => {
    if (!evaluations || evaluations.length === 0 || !progressRef.current) return;
    progressRef.current(evaluationProgressCounts(evaluations));
  }, [evaluations]);

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!evaluations || evaluations.length === 0) {
    return <Typography variant="body2" color="text.secondary">No evaluations found for this identity.</Typography>;
  }

  const rows = evaluations.map((e) => {
    const isOpen = openId === e.checkin_id;
    const warnings = (e.warnings || []).map((w) => WARNING_TEXT[w] || w);
    // Dash already validates this evaluation under the same typed name: it is listed for context but
    // is not part of this review and is not counted. No class action is offered on it, unless a
    // reconciliation already exists and must stay correctable.
    const inScope = isInReviewScope(e);
    const canInspect = inScope || e.is_reconciled;
    return (
      <React.Fragment key={e.checkin_id}>
        <tr ref={(el) => { if (el) rowRefs.current.set(e.checkin_id, el); else rowRefs.current.delete(e.checkin_id); }}>
          <td className="text-start text-secondary" style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.class_date)}</td>
          {/* Raw Form values, verbatim. The whole point of this row is to compare them with Dash,
              so a blank facility stays visibly blank rather than being filled in from anywhere. */}
          <td className="text-start">
            {e.facility_name || <span className="text-secondary">no facility</span>}
            {e.facility_source && e.facility_source !== 'warehouse' && (
              <Chip size="small" label={e.facility_source} variant="outlined" sx={{ ml: 0.75 }} />
            )}
          </td>
          <td className="text-start text-secondary">
            {e.class_time || '—'}
            {e.time_parse_status === 'refused' && (
              <Chip size="small" label="unreadable" color="warning" variant="outlined" sx={{ ml: 0.75 }} />
            )}
          </td>
          <td className="text-start text-secondary">
            {e.program}{e.rubric_name ? ` · ${e.rubric_name}` : ''}
            {/* The evaluator's own level answer, verbatim. Absent on an older API response. */}
            {e.form_class_type != null && String(e.form_class_type).trim() !== '' && (
              <Typography variant="caption" color="text.secondary" component="div">
                Form class type: {e.form_class_type}
              </Typography>
            )}
          </td>
          <td className="text-start text-secondary">{e.typed_coach_name || '—'}</td>
          <td className="text-start text-secondary" style={{ maxWidth: 220 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.evaluator_email || ''}>
              {e.evaluator_email || '—'}
            </div>
            <Typography variant="caption" color="text.disabled">{fmtDate(e.submitted_at)}</Typography>
          </td>
          <td className="text-start">
            {e.is_reconciled ? (
              <>
                {/* Reviewed = BOTH judgments recorded, whatever they are. Never "matched to Dash". */}
                {savedIds.has(e.checkin_id) && (
                  <Chip size="small" label="Saved ✓" color="success" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                )}
                {evaluationRowReviewState(e) === 'reviewed'
                  ? <Chip size="small" label="Reviewed ✓" color="success" sx={{ mr: 0.5, mb: 0.5 }} />
                  : <Chip size="small" label="Partly reviewed — coach not decided" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />}
                {classLine(e)}
                <Typography variant="caption" color="text.secondary" component="div">
                  {coachLine(e)}
                </Typography>
                {warnings.length > 0 && (
                  <Typography variant="caption" color="warning.main" component="div">{warnings.join('; ')}</Typography>
                )}
                {/* Driven by the server's reporting_integration_pending, so this claim cannot
                    drift from what the warehouse is actually doing. */}
                <Typography variant="caption" color="text.disabled" component="div">
                  {integrationPending
                    ? 'Recorded — pending reporting integration'
                    : 'Saved — used in reporting after the next successful check-in warehouse build'}
                </Typography>
              </>
            ) : inScope ? (
              <Typography variant="caption" color="text.disabled">Not reviewed</Typography>
            ) : (
              <Chip size="small" variant="outlined" label="Already validated in Dash — outside this review" />
            )}
          </td>
          <td className="text-start">
            {canInspect && (
              <Button size="small" variant={isOpen ? 'outlined' : 'text'}
                onClick={() => { setAdvancedTo(null); setOpenId(isOpen ? null : e.checkin_id); }}>
                {isOpen ? 'Close' : 'Inspect class'}
              </Button>
            )}
          </td>
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={8} className="text-start">
              <CoachIdentityCandidatePanel evaluation={e} facilities={facilities} onResolved={handleResolved}
                onSaved={handleSaved} focusOnMount={advancedTo === e.checkin_id}
                identityRuleActive={Boolean(row.decision)} integrationPending={integrationPending} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="h3">Evaluation reviews (one evaluation at a time)</Typography>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Evaluations ({progress.evaluation_count}) — {progress.evaluation_reviewed_count} reviewed ·{' '}
        {progress.class_reconciled_count} with a class
        recorded · {progress.coach_resolved_count} coach attribution{progress.coach_resolved_count === 1 ? '' : 's'} resolved
        {outOfScopeCount > 0 && ` · ${outOfScopeCount} already validated in Dash (not counted)`}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Each row is one submitted evaluation. Repeated evaluations of the same class are expected and
        all are kept. Reconciling a class here applies to that evaluation only. Recording a class
        without a coach counts toward Class audit, not Coach attribution.
      </Typography>
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th className="text-start">Class date</th>
              <th className="text-start">Form facility</th>
              <th className="text-start">Form time</th>
              <th className="text-start">Program</th>
              <th className="text-start">Typed coach</th>
              <th className="text-start">Evaluator</th>
              <th className="text-start">Reconciliation</th>
              <th className="text-start" />
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </Box>
  );
}
